// Supervision nationale complète (M24, lot L6-A, D-270 à D-274) : vues agrégées, indicateurs comparables,
// normalisations explicites, masquage des petits effectifs, grandes masses financières et partage à trois
// niveaux. Chaque indicateur comparable renvoie à sa fiche du registre unique (indicateurs.ts) : la liste
// locale de la collectivité et l'agrégat national appliquent la même définition (R16).
import type { CodeModule } from './administration';
import type { CodeIndicateur } from './indicateurs';

/** Vues matérialisées du schéma agregats, rafraîchies sous le rôle cc_agregation. */
export const VUES_AGREGATS = [
  'deploiement',
  'dossiers',
  'services_numerises',
  'interventions',
  'projets',
  'deliberations',
  'anomalies',
  'finances',
  'effectifs',
  'renseignement',
] as const;
export type VueAgregat = (typeof VUES_AGREGATS)[number];

export const LIBELLES_VUES_AGREGATS: Record<VueAgregat, string> = {
  deploiement: 'Déploiement et modules activés',
  dossiers: 'Volumes et délais des dossiers',
  services_numerises: 'Services numérisés (dépôts en ligne)',
  interventions: 'Interventions',
  projets: 'Projets de travaux',
  deliberations: 'Délibérations transmises',
  anomalies: 'Anomalies significatives',
  finances: 'Grandes masses financières',
  effectifs: 'Effectifs du personnel',
  renseignement: 'Sources renseignées par collectivité',
};

export const ETATS_VUE_AGREGAT = ['rafraichie', 'sources_absentes', 'erreur'] as const;
export type EtatVueAgregat = (typeof ETATS_VUE_AGREGAT)[number];

/** Normalisations explicites d'un indicateur (H7-07) ; aucune n'est appliquée sans être demandée. */
export const NORMALISATIONS = ['aucune', 'habitant', 'agent', 'module'] as const;
export type Normalisation = (typeof NORMALISATIONS)[number];

export const LIBELLES_NORMALISATION: Record<Normalisation, string> = {
  aucune: 'Valeur brute, sans normalisation',
  habitant: 'Pour 10 000 habitants déclarés',
  agent: 'Par agent en poste',
  module: 'Par module activé',
};

/** Dénominateur de chaque normalisation, tel qu'il est affiché et exporté. */
export const DENOMINATEURS_NORMALISATION: Record<Normalisation, string | null> = {
  aucune: null,
  habitant: 'Population déclarée au référentiel des collectivités',
  agent: 'Agents en poste au registre du personnel (PER-03)',
  module: 'Nombre de modules activés dans la collectivité',
};

/** Multiplicateur de la valeur normalisée (10 000 habitants). */
export const FACTEURS_NORMALISATION: Record<Normalisation, number> = { aucune: 1, habitant: 10_000, agent: 1, module: 1 };

/**
 * Seuil de masquage des petits effectifs RH ou sociaux (D-271, hypothèse technique) : une cellule
 * strictement inférieure n'est pas détaillée, ni directement, ni comme dénominateur, ni dans un total.
 */
export const SEUIL_PETIT_EFFECTIF = 5;

/** Masses du paramètre de partage (troisième niveau). */
export const MASSES_PARTAGE = ['recettes', 'depenses'] as const;
export type MassePartage = (typeof MASSES_PARTAGE)[number];
export const LIBELLES_MASSE_PARTAGE: Record<MassePartage, string> = { recettes: 'Recettes', depenses: 'Dépenses' };

/** Grandes masses restituées (H7-07) : recettes confirmées, dépenses payées par section. */
export const MASSES_FINANCIERES = ['recettes_confirmees', 'depenses_fonctionnement', 'depenses_investissement'] as const;
export type MasseFinanciere = (typeof MASSES_FINANCIERES)[number];
export const LIBELLES_MASSE_FINANCIERE: Record<MasseFinanciere, string> = {
  recettes_confirmees: 'Recettes confirmées (REC-04)',
  depenses_fonctionnement: 'Dépenses payées de fonctionnement (DEP-02)',
  depenses_investissement: 'Dépenses payées d\'investissement (DEP-02)',
};
export const MASSE_PARTAGE_DE: Record<MasseFinanciere, MassePartage> = {
  recettes_confirmees: 'recettes',
  depenses_fonctionnement: 'depenses',
  depenses_investissement: 'depenses',
};
export const MODULE_MASSE: Record<MassePartage, CodeModule> = { recettes: 'M07', depenses: 'M06' };

/** Niveaux du paramètre de partage (D-272) : national, collectivité, masse ; le plus fin l'emporte. */
export const NIVEAUX_PARTAGE = ['national', 'collectivite', 'masse'] as const;
export type NiveauPartage = (typeof NIVEAUX_PARTAGE)[number];
export const VALEURS_PARTAGE = ['partage', 'non_partage', 'herite'] as const;
export type ValeurPartage = (typeof VALEURS_PARTAGE)[number];
export const LIBELLES_PROVENANCE_PARTAGE: Record<string, string> = {
  national_defaut: 'Défaut national (partagé pour les collectivités actives, H7-07)',
  national: 'Paramètre national',
  collectivite: 'Choix de la collectivité',
  masse: 'Choix de la collectivité pour cette masse',
};

/** Institutions disposant du droit financier (D-273) ; préfectures et gouvernorats n'en ont aucun. */
export const INSTITUTIONS_DROIT_FINANCIER = ['ministere', 'autorite_financiere'] as const;
/** Institutions autorisées à écrire le registre des compétences transférées (D-274). */
export const INSTITUTIONS_ECRITURE_COMPETENCES = ['ministere'] as const;

/** Colonnes financières : jamais présentes dans une réponse ou un export sans droit financier. */
export const COLONNES_FINANCIERES = ['montant', 'moyens_financiers_montant', 'montant_annuel'] as const;

export interface IndicateurSupervision {
  code: CodeIndicateur;
  vue: VueAgregat;
  colonne: string;
  nature: 'periode' | 'instantane';
  normalisations: readonly Normalisation[];
  financier: boolean;
  /** Effectif RH ou social : masqué sous SEUIL_PETIT_EFFECTIF (D-271). */
  petitEffectif: boolean;
  module: CodeModule;
  /** Table source dont la présence d'au moins une ligne vaut « renseigné » (vue renseignement). */
  source: string;
}

export const INDICATEURS_SUPERVISION = [
  { code: 'DOS-09', vue: 'deploiement', colonne: 'demarches_publiees', nature: 'instantane', normalisations: ['aucune', 'habitant', 'module'], financier: false, petitEffectif: false, module: 'M05', source: 'demarche' },
  { code: 'DOS-10', vue: 'services_numerises', colonne: 'dossiers_en_ligne', nature: 'periode', normalisations: ['aucune', 'habitant', 'agent'], financier: false, petitEffectif: false, module: 'M05', source: 'dossier' },
  { code: 'INT-05', vue: 'interventions', colonne: 'interventions_cloturees', nature: 'periode', normalisations: ['aucune', 'habitant', 'agent'], financier: false, petitEffectif: false, module: 'M12', source: 'intervention' },
  { code: 'TRX-02', vue: 'projets', colonne: 'projets_en_cours', nature: 'instantane', normalisations: ['aucune', 'habitant'], financier: false, petitEffectif: false, module: 'M14', source: 'projet_travaux' },
  { code: 'ASS-04', vue: 'deliberations', colonne: 'deliberations_transmises', nature: 'periode', normalisations: ['aucune'], financier: false, petitEffectif: false, module: 'M16', source: 'deliberation' },
  { code: 'REC-08', vue: 'anomalies', colonne: 'anomalies_ouvertes', nature: 'instantane', normalisations: ['aucune'], financier: false, petitEffectif: false, module: 'M07', source: 'anomalie' },
  { code: 'REC-04', vue: 'finances', colonne: 'recettes_confirmees', nature: 'periode', normalisations: ['aucune', 'habitant'], financier: true, petitEffectif: false, module: 'M07', source: 'paiement' },
  { code: 'DEP-02', vue: 'finances', colonne: 'depenses', nature: 'periode', normalisations: ['aucune', 'habitant'], financier: true, petitEffectif: false, module: 'M06', source: 'paiement_depense' },
  { code: 'PER-03', vue: 'effectifs', colonne: 'agents_en_poste', nature: 'instantane', normalisations: ['aucune', 'habitant'], financier: false, petitEffectif: true, module: 'M11', source: 'agent_personnel' },
] as const satisfies readonly IndicateurSupervision[];
export type CodeIndicateurSupervision = (typeof INDICATEURS_SUPERVISION)[number]['code'];
export const CODES_INDICATEURS_SUPERVISION = INDICATEURS_SUPERVISION.map((i) => i.code) as CodeIndicateurSupervision[];

export function indicateurSupervision(code: string): IndicateurSupervision | undefined {
  return (INDICATEURS_SUPERVISION as readonly IndicateurSupervision[]).find((i) => i.code === code);
}

/** État d'une valeur de supervision : jamais zéro à la place d'une source absente. */
export const ETATS_VALEUR_SUPERVISION = ['disponible', 'indisponible', 'masque', 'non_active', 'non_partage', 'non_renseigne', 'source_absente'] as const;
export type EtatValeurSupervision = (typeof ETATS_VALEUR_SUPERVISION)[number];
export const LIBELLES_ETAT_VALEUR: Record<EtatValeurSupervision, string> = {
  disponible: 'Disponible',
  indisponible: 'Indisponible',
  masque: 'Masqué (petit effectif)',
  non_active: 'Module non activé',
  non_partage: 'Non partagé par la collectivité',
  non_renseigne: 'Non renseigné',
  source_absente: 'Source absente',
};

export interface CouvertureSource {
  vue: VueAgregat;
  etat: EtatVueAgregat;
  /** Dernier rafraîchissement réussi (les valeurs restituées en datent, même après une erreur). */
  calcule_le: string | null;
  message: string | null;
}

export interface CouvertureSupervision {
  etat: string;
  calcule_le: string | null;
  message: string | null;
  sources?: CouvertureSource[];
  /** Collectivités du périmètre et des filtres, et celles dont la valeur est disponible. */
  collectivites_perimetre?: number;
  collectivites_couvertes?: number;
}

export interface LigneComparaison {
  collectivite_id: string;
  code_court: string;
  nom: string;
  type: string;
  province_code: string;
  fictive: boolean;
  numerateur: number | null;
  denominateur: number | null;
  valeur: number | null;
  etat: EtatValeurSupervision;
  motif: string | null;
}

export interface ComparaisonSupervision {
  perimetre: string;
  indicateur: { code: string; libelle: string; definition: string; unite: string; nature: string; module: string; financier: boolean; petit_effectif: boolean };
  normalisation: { code: Normalisation; libelle: string; denominateur: string | null; facteur: number };
  periode: { mois_debut: string; mois_fin: string } | null;
  couverture: CouvertureSupervision;
  /** Total brut, seulement sans normalisation, sur une unité additive et si aucune cellule n'est masquée. */
  total: number | null;
  motif_total: string | null;
  elements: LigneComparaison[];
}
